<script>
    import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
    import { fade } from "svelte/transition";
    import { functions } from "@app/configs/firebase.js";
    import { httpsCallable } from "firebase/functions";
    import PaymentMethodTab from "./subscription/PaymentMethodTab.svelte";
    import AgentPluginSection from "./subscription/AgentPluginSection.svelte";
    import Swal from "sweetalert2";
    import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
    import * as m from "@app/paraglide/messages.js";
    import { entitlements, isFreePlan } from "@app/lib/entitlements.js";
    import { openExternal, BILLING_PORTAL_URL } from "@app/lib/upgradePrompt.js";

    /* Pricing v2: seats on the plan, and Free (nothing to cancel; upgrade instead). */
    let seats = $derived(entitlements()?.seats ?? null);
    let onFreePlan = $derived(isFreePlan());

    let selectedTab = $state("subscription");
    let loading = $state(true);
    let subscriptionData = $state(null);
    let error = $state(null);

    // Action loading states
    let activating = $state(false);
    let cancelling = $state(false);
    let payingInvoice = $state(false);

    // Fetch subscription data on mount
    $effect(() => {
        fetchSubscriptionData();
    });

    async function fetchSubscriptionData() {
        loading = true;
        error = null;
        try {
            const result = await httpsCallable(functions, "v8GetSubscriptionData")({});
            subscriptionData = result.data;
        } catch (err) {
            console.error("Failed to fetch subscription data:", err);
            error = err.message || "Failed to load subscription data.";
            subscriptionData = { found: false };
        } finally {
            loading = false;
        }
    }

    async function handleActivateTrial() {
        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.trial,
            title: m.activate_confirm_title(),
            text: m.activate_confirm_text(),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.activate_subscription(),
            cancelButtonText: m.cancel(),
        });

        if (!result.isConfirmed) return;

        activating = true;
        try {
            await httpsCallable(functions, "v8ActivateTrial")({});
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.success(),
                text: m.activate_success(),
                icon: "success",
            });
            await fetchSubscriptionData();
        } catch (err) {
            console.error("Failed to activate trial:", err);
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.error(),
                text: err.message || "Failed to activate subscription.",
                icon: "error",
            });
        } finally {
            activating = false;
        }
    }

    async function handleCancelSubscription() {
        const result = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.destructive,
            title: m.cancel_confirm_title(),
            text: m.cancel_confirm_text(),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.cancel_subscription(),
            cancelButtonText: m.cancel(),
        });

        if (!result.isConfirmed) return;

        cancelling = true;
        try {
            await httpsCallable(functions, "v8CancelSubscription")({});
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.cancellation_scheduled(),
                text: m.cancel_success(),
                icon: "success",
            });
            await fetchSubscriptionData();
        } catch (err) {
            console.error("Failed to cancel subscription:", err);
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.error(),
                text: err.message || "Failed to cancel subscription.",
                icon: "error",
            });
        } finally {
            cancelling = false;
        }
    }

    async function handlePayOverdueInvoice() {
        payingInvoice = true;
        try {
            const result = await httpsCallable(functions, "v8PayOverdueInvoice")({});
            if (result.data?.paymentUrl) {
                window.open(result.data.paymentUrl, "_blank");
            }
        } catch (err) {
            console.error("Failed to get payment URL:", err);
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.error(),
                text: err.message || "Failed to retrieve payment URL.",
                icon: "error",
            });
        } finally {
            payingInvoice = false;
        }
    }

    function formatDate(timestamp) {
        if (!timestamp) return "—";
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    }

    function formatCurrency(amount, currency) {
        if (amount == null) return "—";
        return new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currency || "usd",
        }).format(amount / 100);
    }

    function getStatusBadgeClass(status) {
        switch (status) {
            case "active":
                return "bg-success";
            case "trialing":
                return "bg-warning text-dark";
            case "past_due":
                return "bg-danger";
            case "canceled":
                return "bg-secondary";
            default:
                return "bg-info";
        }
    }

    function getStatusLabel(status) {
        switch (status) {
            case "active":
                return m.subscription_active();
            case "trialing":
                return m.subscription_trialing();
            case "past_due":
                return m.subscription_past_due();
            case "canceled":
                return m.subscription_cancelled();
            default:
                return status;
        }
    }
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'subscriptionManagement'
        ? 'show d-block'
        : ''}"
    role="dialog"
    aria-labelledby="subscriptionModalTitle"
    tabindex="-1"
    id="SubscriptionManagementModal"
>
    <div
        class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        role="document"
    >
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="subscriptionModalTitle">
                    {m.subscription_management()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={hideModal}
                ></button>
            </div>
            <div class="modal-body">
                {#if loading}
                    <div
                        class="d-flex justify-content-center align-items-center"
                        style="min-height: 300px;"
                    >
                        <div class="text-center">
                            <div
                                class="spinner-border text-primary mb-3"
                                role="status"
                            >
                                <span class="visually-hidden"
                                    >{m.loading_subscription_data()}</span
                                >
                            </div>
                            <p class="text-muted">
                                {m.loading_subscription_data()}
                            </p>
                        </div>
                    </div>
                {:else if !subscriptionData?.found}
                    <div
                        class="d-flex justify-content-center align-items-center"
                        style="min-height: 300px;"
                    >
                        <div class="text-center text-muted">
                            <i
                                class="bi bi-exclamation-circle"
                                style="font-size: 3rem;"
                            ></i>
                            <p class="mt-3">{m.no_subscription_access()}</p>
                            <p>If you think this is a mistake please visit our <a href="https://billing.stripe.com/p/login/eVqaEX0YyczidOadSsfQI00" target="_blank" rel="noopener noreferrer">self-serve portal</a> or contact support:</p>
                            <p><a href="https://billing.stripe.com/p/login/eVqaEX0YyczidOadSsfQI00" target="_blank" rel="noopener noreferrer">Self-Serve Portal</a></p>
                            <p><a href="mailto:support@closedcaptioncreator.com">support@closedcaptioncreator.com</a></p>
                        </div>
                    </div>
                {:else}
                    <div class="row">
                        <!-- Tab Navigation Sidebar -->
                        <div class="col-3">
                            <nav>
                                <ul
                                    class="nav nav-pills flex-column"
                                    role="tablist"
                                    aria-label={m.subscription_management()}
                                >
                                    <li class="nav-item" role="presentation">
                                        <a
                                            class="nav-link {selectedTab ===
                                            'subscription'
                                                ? 'active'
                                                : ''}"
                                            id="subscription-tab"
                                            href="#!/"
                                            role="tab"
                                            aria-selected={selectedTab ===
                                                "subscription"}
                                            aria-controls="subscription-panel"
                                            onclick={() => {
                                                selectedTab = "subscription";
                                            }}>{m.subscription_tab()}</a
                                        >
                                    </li>
                                    <li class="nav-item" role="presentation">
                                        <a
                                            class="nav-link {selectedTab ===
                                            'invoices'
                                                ? 'active'
                                                : ''}"
                                            id="invoices-tab"
                                            href="#!/"
                                            role="tab"
                                            aria-selected={selectedTab ===
                                                "invoices"}
                                            aria-controls="invoices-panel"
                                            onclick={() => {
                                                selectedTab = "invoices";
                                            }}>{m.invoices_tab()}</a
                                        >
                                    </li>
                                    <li class="nav-item" role="presentation">
                                        <a
                                            class="nav-link {selectedTab ===
                                            'paymentMethod'
                                                ? 'active'
                                                : ''}"
                                            id="paymentMethod-tab"
                                            href="#!/"
                                            role="tab"
                                            aria-selected={selectedTab ===
                                                "paymentMethod"}
                                            aria-controls="paymentMethod-panel"
                                            onclick={() => {
                                                selectedTab = "paymentMethod";
                                            }}>{m.payment_method_tab()}</a
                                        >
                                    </li>
                                    <li class="nav-item" role="presentation">
                                        <a
                                            class="nav-link {selectedTab ===
                                            'support'
                                                ? 'active'
                                                : ''}"
                                            id="support-tab"
                                            href="#!/"
                                            role="tab"
                                            aria-selected={selectedTab ===
                                                "support"}
                                            aria-controls="support-panel"
                                            onclick={() => {
                                                selectedTab = "support";
                                            }}>{m.support_tab()}</a
                                        >
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        <!-- Tab Content -->
                        <div class="col-9">
                            <!-- Subscription Tab -->
                            <div
                                role="tabpanel"
                                aria-labelledby="subscription-tab"
                                id="subscription-panel"
                                class:d-none={selectedTab !== "subscription"}
                            >
                                {#if selectedTab === "subscription"}
                                    <h5 class="mb-4">
                                        {m.subscription_tab()}
                                    </h5>

                                    <div class="card mb-3">
                                        <div class="card-body">
                                            <div class="row mb-2">
                                                <div
                                                    class="col-4 text-muted fw-semibold"
                                                >
                                                    {m.current_plan()}
                                                </div>
                                                <div class="col-8">
                                                    {subscriptionData
                                                        .subscription.planName}
                                                </div>
                                            </div>
                                            <div class="row mb-2">
                                                <div
                                                    class="col-4 text-muted fw-semibold"
                                                >
                                                    {m.subscription_status()}
                                                </div>
                                                <div class="col-8">
                                                    <span
                                                        class="badge {getStatusBadgeClass(subscriptionData.subscription.status)}"
                                                    >
                                                        {getStatusLabel(
                                                            subscriptionData
                                                                .subscription
                                                                .status,
                                                        )}
                                                    </span>
                                                    {#if subscriptionData.subscription.cancelAtPeriodEnd}
                                                        <span
                                                            class="badge bg-secondary ms-2"
                                                            >{m.subscription_cancelled()}</span
                                                        >
                                                    {/if}
                                                </div>
                                            </div>
                                            <div class="row mb-2">
                                                <div
                                                    class="col-4 text-muted fw-semibold"
                                                >
                                                    {m.period_end()}
                                                </div>
                                                <div class="col-8">
                                                    {formatDate(
                                                        subscriptionData
                                                            .subscription
                                                            .currentPeriodEnd,
                                                    )}
                                                </div>
                                            </div>
                                            {#if subscriptionData.subscription.status === "trialing" && subscriptionData.subscription.trialEnd}
                                                <div class="row mb-2">
                                                    <div
                                                        class="col-4 text-muted fw-semibold"
                                                    >
                                                        {m.trial_ends()}
                                                    </div>
                                                    <div class="col-8">
                                                        {formatDate(
                                                            subscriptionData
                                                                .subscription
                                                                .trialEnd,
                                                        )}
                                                    </div>
                                                </div>
                                            {/if}
                                            {#if seats && seats.limit}
                                                <div class="row mb-2">
                                                    <div
                                                        class="col-4 text-muted fw-semibold"
                                                    >
                                                        {m.subscription_seats()}
                                                    </div>
                                                    <div class="col-8">
                                                        {m.team_manager_seats_used({ used: seats.used, limit: seats.limit })}
                                                    </div>
                                                </div>
                                            {/if}
                                        </div>
                                    </div>

                                    <!-- Action Buttons -->
                                    <div class="d-flex gap-2 flex-wrap">
                                        {#if onFreePlan}
                                            <button
                                                class="btn btn-primary"
                                                onclick={() => openExternal(BILLING_PORTAL_URL)}
                                            >
                                                <i class="bi bi-arrow-up-circle me-1"></i>
                                                {m.upgrade_view_plans()}
                                            </button>
                                        {/if}
                                        {#if subscriptionData.subscription.status === "trialing"}
                                            <button
                                                class="btn btn-primary"
                                                onclick={handleActivateTrial}
                                                disabled={activating}
                                            >
                                                {#if activating}
                                                    <span
                                                        class="spinner-border spinner-border-sm me-1"
                                                        role="status"
                                                    ></span>
                                                    {m.processing()}
                                                {:else}
                                                    <i
                                                        class="bi bi-check-circle me-1"
                                                    ></i>
                                                    {m.activate_subscription()}
                                                {/if}
                                            </button>
                                        {/if}

                                        {#if subscriptionData.subscription.status === "past_due"}
                                            <button
                                                class="btn btn-warning"
                                                onclick={handlePayOverdueInvoice}
                                                disabled={payingInvoice}
                                            >
                                                {#if payingInvoice}
                                                    <span
                                                        class="spinner-border spinner-border-sm me-1"
                                                        role="status"
                                                    ></span>
                                                    {m.processing()}
                                                {:else}
                                                    <i
                                                        class="bi bi-credit-card me-1"
                                                    ></i>
                                                    {m.pay_overdue_invoice()}
                                                {/if}
                                            </button>
                                        {/if}

                                        {#if !onFreePlan && !subscriptionData.subscription.cancelAtPeriodEnd && subscriptionData.subscription.status !== "canceled"}
                                            <button
                                                class="btn btn-outline-danger"
                                                onclick={handleCancelSubscription}
                                                disabled={cancelling}
                                            >
                                                {#if cancelling}
                                                    <span
                                                        class="spinner-border spinner-border-sm me-1"
                                                        role="status"
                                                    ></span>
                                                    {m.processing()}
                                                {:else}
                                                    <i
                                                        class="bi bi-x-circle me-1"
                                                    ></i>
                                                    {m.cancel_subscription()}
                                                {/if}
                                            </button>
                                        {/if}
                                    </div>
                                    <AgentPluginSection />
                                {/if}
                            </div>

                            <!-- Invoices Tab -->
                            <div
                                role="tabpanel"
                                aria-labelledby="invoices-tab"
                                id="invoices-panel"
                                class:d-none={selectedTab !== "invoices"}
                            >
                                {#if selectedTab === "invoices"}
                                    <h5 class="mb-4">{m.invoices_tab()}</h5>

                                    {#if subscriptionData.invoices && subscriptionData.invoices.length > 0}
                                        <div class="table-responsive">
                                            <table
                                                class="table table-sm table-hover"
                                            >
                                                <thead>
                                                    <tr>
                                                        <th
                                                            >{m.invoice_date()}</th
                                                        >
                                                        <th
                                                            >{m.invoice_amount()}</th
                                                        >
                                                        <th
                                                            >{m.invoice_status()}</th
                                                        >
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {#each subscriptionData.invoices as invoice}
                                                        <tr>
                                                            <td
                                                                >{formatDate(
                                                                    invoice.date,
                                                                )}</td
                                                            >
                                                            <td
                                                                >{formatCurrency(
                                                                    invoice.amountPaid ||
                                                                        invoice.amountDue,
                                                                    invoice.currency,
                                                                )}</td
                                                            >
                                                            <td>
                                                                <span
                                                                    class="badge {invoice.status ===
                                                                    'paid'
                                                                        ? 'bg-success'
                                                                        : invoice.status ===
                                                                            'open'
                                                                          ? 'bg-warning text-dark'
                                                                          : 'bg-secondary'}"
                                                                >
                                                                    {invoice.status}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {#if invoice.invoicePdf}
                                                                    <a
                                                                        href={invoice.invoicePdf}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        class="btn btn-sm btn-outline-primary"
                                                                    >
                                                                        <i
                                                                            class="bi bi-download me-1"
                                                                        ></i>
                                                                        {m.download_invoice()}
                                                                    </a>
                                                                {/if}
                                                            </td>
                                                        </tr>
                                                    {/each}
                                                </tbody>
                                            </table>
                                        </div>
                                    {:else}
                                        <p class="text-muted">
                                            {m.no_invoices()}
                                        </p>
                                    {/if}
                                {/if}
                            </div>

                            <!-- Payment Method Tab -->
                            <div
                                role="tabpanel"
                                aria-labelledby="paymentMethod-tab"
                                id="paymentMethod-panel"
                                class:d-none={selectedTab !== "paymentMethod"}
                            >
                                {#if selectedTab === "paymentMethod"}
                                    <PaymentMethodTab
                                        paymentMethod={subscriptionData.paymentMethod}
                                        onCardUpdated={fetchSubscriptionData}
                                    />
                                {/if}
                            </div>

                            <!-- Support Tab -->
                            <div
                                role="tabpanel"
                                aria-labelledby="support-tab"
                                id="support-panel"
                                class:d-none={selectedTab !== "support"}
                            >
                                {#if selectedTab === "support"}
                                    <h5 class="mb-4">{m.support_tab()}</h5>
                                    <div class="card">
                                        <div class="card-body">
                                            <p class="mb-0">
                                                {m.billing_support_message()}
                                                <a
                                                    href="mailto:billing@closedcaptioncreator.com"
                                                    >billing@closedcaptioncreator.com</a
                                                >
                                            </p>
                                        </div>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="css">
    .modal-body {
        min-height: 60vh;
    }
</style>
